import { ResolvedPaymentPlan, ResolvedPaymentPlanTermRate } from '@src/types/ResolvedPaymentPlan'

export interface CreditTilePromoRate {
  /** Coefficient, as Merchants sends it (0.0299 for 2.99%). */
  promoRate: number
  durationMonths: number
  /** Other terms may carry a different rate: this is the best of them, not a commitment. */
  isLowestAvailable: boolean
}

export interface CreditTilePlan {
  promoRate: CreditTilePromoRate | null
  /** Null while the credit decision has not set a max term. */
  maxTermMonths: number | null
  /** Null when the application is on the regular plan. */
  planLabel: string | null
}

interface Params {
  /** The plan the application carries, resolved by Merchants; null on the regular plan. */
  appliedPlan: ResolvedPaymentPlan | null
  worksheetTerm: number | null
  /** The merchant's best applicable promo rate, when one may still be offered. */
  lowestAvailableRate: ResolvedPaymentPlanTermRate | null
  /** Max term from the regular plan rules, as decided by the credit tree. */
  decisionMaxTerm: number | null
}

const toPromoRate = (termRate: ResolvedPaymentPlanTermRate, isLowestAvailable: boolean): CreditTilePromoRate => ({
  promoRate: termRate.promoRate,
  durationMonths: termRate.promoDurationMonths ?? termRate.termMonths,
  isLowestAvailable,
})

const lowestAvailable = (rate: ResolvedPaymentPlanTermRate | null) => (rate ? toPromoRate(rate, true) : null)

/**
 * What the Credit tile shows about the payment plan, whichever kind of plan the application carries.
 *
 * A plan with a single term rate fixes its own term. A plan with several leaves the term to the
 * worksheet: until one picks it, the tile falls back to the regular rules and the best rate on offer.
 */
export const getCreditTilePlan = ({
  appliedPlan,
  worksheetTerm,
  lowestAvailableRate,
  decisionMaxTerm,
}: Params): CreditTilePlan => {
  if (appliedPlan == null) {
    return { promoRate: lowestAvailable(lowestAvailableRate), maxTermMonths: decisionMaxTerm, planLabel: null }
  }

  const { termRates } = appliedPlan
  if (termRates.length === 1) {
    return {
      promoRate: toPromoRate(termRates[0], false),
      maxTermMonths: termRates[0].termMonths,
      planLabel: appliedPlan.label,
    }
  }

  const worksheetTermRate = termRates.find((rate) => rate.termMonths === worksheetTerm)
  return {
    promoRate: worksheetTermRate ? toPromoRate(worksheetTermRate, false) : lowestAvailable(lowestAvailableRate),
    maxTermMonths: worksheetTerm ?? decisionMaxTerm,
    planLabel: appliedPlan.label,
  }
}
